import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, Trash2, Search, ChevronRight, Building2, Loader2, Sparkles, Send, Mail,
  Calendar, Users, X, ArrowRight, FlaskConical, UserPlus, CheckCircle2, ChevronDown, ChevronUp, Globe, Ban, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';


// ─────────────────────────────────────────────
//  CampaignFocusTasEditor — inline chip-editor under the campaign header.
//  Sets `focus_therapeutic_areas` on the campaign which drives AI auto-scope
//  every time a biotech is added. Lets the user reset / re-pick TAs after
//  the initial create.
// ─────────────────────────────────────────────
function CampaignFocusTasEditor({ campaignId, initial, onSaved }) {
  const [tas, setTas] = useState(initial || []);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => { setTas(initial || []); }, [initial]);

  const addTa = () => {
    const t = input.trim();
    if (!t) return;
    if (tas.find(x => x.toLowerCase() === t.toLowerCase())) { setInput(''); return; }
    setTas(prev => [...prev, t]);
    setInput('');
  };
  const removeTa = (t) => setTas(prev => prev.filter(x => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/campaigns/${campaignId}`, { focus_therapeutic_areas: tas }, { withCredentials: true });
      onSaved && onSaved(r.data.focus_therapeutic_areas || tas);
      setEditing(false);
      toast.success('Focus TAs updated');
    } catch { toast.error('Failed to save focus TAs'); }
    setSaving(false);
  };

  if (!editing) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid="campaign-focus-tas-view">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Focus TAs:</span>
        {(initial || []).length === 0 ? (
          <span className="text-[11px] text-slate-400 italic">none — AI auto-scope disabled</span>
        ) : (
          (initial || []).map(t => (
            <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[11px]" data-testid={`campaign-focus-ta-${t}`}>{t}</span>
          ))
        )}
        <button
          onClick={() => setEditing(true)}
          className="text-[11px] text-[#00709B] hover:underline ml-1"
          data-testid="campaign-focus-tas-edit-btn"
        >
          {(initial || []).length === 0 ? '+ Set' : 'Edit'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 p-2 rounded border border-sky-200 bg-sky-50/50" data-testid="campaign-focus-tas-editor">
      <div className="flex flex-wrap gap-1 mb-1.5">
        {tas.map(t => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white text-sky-700 border border-sky-200 text-[11px]">
            {t}
            <button onClick={() => removeTa(t)} className="hover:text-sky-900"><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTa(); } }}
          placeholder="e.g. Oncology — Enter to add"
          className="h-7 text-xs"
          data-testid="campaign-focus-tas-input"
        />
        <Button type="button" size="sm" variant="outline" onClick={addTa} disabled={!input.trim()} className="h-7 text-xs">Add</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving} className="h-7 text-xs bg-slate-900 text-white" data-testid="campaign-focus-tas-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setTas(initial || []); }} className="h-7 text-xs">Cancel</Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
//  BiotechCampaignCard — expandable card showing the biotech and its
//  assets with per-asset checkboxes so the user can pitch a subset.
// ─────────────────────────────────────────────

// ─── Campaign context editor ─────────────────────────────────────────────
// Free-text steer injected verbatim into the Claude draft prompt. Lets the
// user flip tone post-event ("ASCO concluded — frame as polite follow-up")
// without a code change. Saved via PATCH /campaigns/{id} → `campaign_context`.
function CampaignContextEditor({ campaignId, initial, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API}/campaigns/${campaignId}`,
        { campaign_context: value },
        { withCredentials: true }
      );
      onSaved && onSaved(r.data.campaign_context ?? value);
      setEditing(false);
      toast.success('Campaign context saved — re-click "Draft emails for all" to regenerate with the new tone');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save campaign context');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const preview = (initial || '').trim();
    return (
      <div className="mt-1.5 flex items-start gap-2" data-testid="campaign-context-display">
        <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wide mt-0.5">Tone:</span>
        {preview ? (
          <span className="text-[11px] text-slate-700 italic flex-1 line-clamp-2" title={preview}>
            "{preview.length > 140 ? preview.slice(0, 140) + '…' : preview}"
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic flex-1">
            none — AI uses the default pre-conference tone
          </span>
        )}
        <button
          onClick={() => { setValue(initial || ''); setEditing(true); }}
          className="text-[11px] text-[#00709B] hover:underline shrink-0"
          data-testid="campaign-context-edit-btn"
        >
          {preview ? 'Edit' : '+ Set'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1.5 p-2 rounded border border-violet-200 bg-violet-50/50" data-testid="campaign-context-editor">
      <p className="text-[10px] text-slate-600 mb-1.5">
        <strong>Campaign context / tone steer</strong> — injected verbatim into the AI prompt. Use this to flip tone post-event, add narrative anchors, or change the CTA. Example:&nbsp;
        <em>"ASCO 2026 has concluded. Frame as a polite post-event follow-up: open with 'Although we could not meet at ASCO 2026, I wanted to introduce…' and replace the at-conference CTA with 'Would you like to evaluate any of the assets mentioned above?'"</em>
      </p>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={4}
        maxLength={2000}
        className="w-full text-[11px] p-1.5 border border-slate-300 rounded bg-white"
        placeholder="e.g. ASCO has concluded — frame as polite post-event follow-up…"
        data-testid="campaign-context-textarea"
      />
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="text-[10px] text-slate-400">{value.length}/2000</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => { setValue(initial || ''); setEditing(false); }}
            disabled={saving}
            className="text-[11px] px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
            data-testid="campaign-context-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="text-[11px] px-2 py-0.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            data-testid="campaign-context-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


function BiotechCampaignCard({ cb, campaignId, focusTas, onRemove, onSaveAssetSelection, onDraftOne, onSendOne }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(null); // local selection state while editing
  const [autoScoping, setAutoScoping] = useState(false);
  const [scopeRationales, setScopeRationales] = useState({}); // {asset_id: rationale} from latest auto-scope run
  const assets = cb.assets || [];
  const selectedCount = draft
    ? draft.size
    : cb.selected_asset_count ?? assets.length;
  const pc = cb.primary_contact;

  const toggleAsset = (aid) => {
    setDraft(prev => {
      const next = new Set(prev ?? new Set(assets.filter(a => a.selected).map(a => a.id)));
      if (next.has(aid)) next.delete(aid);
      else next.add(aid);
      return next;
    });
  };
  const selectAll = () => setDraft(new Set(assets.map(a => a.id)));
  const selectNone = () => setDraft(new Set());
  const dirty = draft !== null;

  const save = () => {
    onSaveAssetSelection(Array.from(draft));
    setDraft(null);
  };

  const runAutoScope = async () => {
    if (!focusTas || focusTas.length === 0) {
      toast.error('Set campaign focus TAs first');
      return;
    }
    setAutoScoping(true);
    try {
      const r = await axios.post(`${API}/campaigns/${campaignId}/biotechs/${cb.biotech_id}/auto-scope`, {}, { withCredentials: true });
      const classifications = r.data?.classifications || [];
      const inScopeIds = classifications.filter(c => c.in_scope).map(c => c.id);
      const rationaleMap = {};
      classifications.forEach(c => { rationaleMap[c.id] = c.rationale; });
      setScopeRationales(rationaleMap);
      setDraft(new Set(inScopeIds));
      setExpanded(true);
      toast.success(`AI scope: ${inScopeIds.length} of ${classifications.length} asset(s) in ${focusTas.join(', ')} — review and Save`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'AI auto-scope failed';
      toast.error(msg);
    }
    setAutoScoping(false);
  };

  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100" data-testid={`biotech-row-${cb.biotech_id}`}>
      <div className="flex items-center gap-2 py-2 px-3">
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-slate-400 hover:text-slate-700 p-0.5"
          data-testid={`biotech-expand-${cb.biotech_id}`}>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-900 truncate">
            {cb.biotech?.company_name}
            {pc?.email && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-normal text-slate-500" data-testid={`biotech-primary-contact-${cb.biotech_id}`}>
                <span className="text-slate-300">·</span>
                {pc.name && <span className="text-slate-700 font-medium">{pc.name}</span>}
                <a
                  href={`mailto:${pc.email}`}
                  onClick={e => e.stopPropagation()}
                  className="text-[#00709B] hover:underline truncate max-w-[180px]"
                  title={pc.designation ? `${pc.designation} — ${pc.reason || ''}` : (pc.reason || '')}
                  data-testid={`biotech-primary-email-${cb.biotech_id}`}
                >
                  {pc.email}
                </a>
              </span>
            )}
          </p>
          <p className="text-[10px] text-slate-500 truncate">
            {cb.biotech?.therapeutic_area || '—'}
            {' · '}
            <span className="text-emerald-700 font-medium">{selectedCount}</span>
            {` of ${assets.length} asset${assets.length !== 1 ? 's' : ''} in scope`}
            {cb.biotech?.do_not_contact?.flagged && (
              <span className="ml-2 inline-flex items-center gap-0.5 text-rose-600 font-medium" title={cb.biotech.do_not_contact.reason || ''}>
                <Ban className="w-2.5 h-2.5" /> DNC
              </span>
            )}
          </p>
        </div>
        {focusTas && focusTas.length > 0 && (
          <button
            onClick={runAutoScope}
            disabled={autoScoping}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 disabled:opacity-60"
            title={`Re-classify assets against campaign focus: ${focusTas.join(', ')}`}
            data-testid={`biotech-autoscope-${cb.biotech_id}`}
          >
            {autoScoping
              ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Classifying…</>
              : <><Sparkles className="w-2.5 h-2.5" /> Apply scope (AI)</>}
          </button>
        )}
        {pc?.email && !cb.biotech?.do_not_contact?.flagged && (
          <a
            href={`mailto:${pc.email}`}
            onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-200"
            title={`Open your mail client to ${pc.email} (not an outreach campaign — for one-off reply)`}
            data-testid={`biotech-mailto-${cb.biotech_id}`}
          >
            <Mail className="w-2.5 h-2.5" /> Mail
          </a>
        )}
        <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1" data-testid={`biotech-remove-${cb.biotech_id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {expanded && (
        <div className="border-t border-slate-200 px-3 py-2 space-y-1.5">
          <div className="flex items-center gap-3 pb-1 text-[10px]">
            <button onClick={selectAll} className="text-[#00709B] hover:underline" data-testid={`biotech-selectall-${cb.biotech_id}`}>Select all</button>
            <button onClick={selectNone} className="text-slate-500 hover:underline" data-testid={`biotech-selectnone-${cb.biotech_id}`}>Select none</button>
            {dirty && (
              <>
                <span className="flex-1" />
                <button onClick={() => setDraft(null)} className="text-slate-500 hover:underline">Discard</button>
                <button onClick={save} className="text-emerald-700 font-medium hover:underline" data-testid={`biotech-save-assets-${cb.biotech_id}`}>Save</button>
              </>
            )}
          </div>
          {assets.length === 0 ? (
            <p className="text-[11px] text-slate-400 py-1">No assets in DB for this biotech.</p>
          ) : (
            assets.map(a => {
              const isChecked = draft ? draft.has(a.id) : a.selected;
              const aiNote = scopeRationales[a.id];
              return (
                <label
                  key={a.id}
                  className="flex items-start gap-2 py-1 cursor-pointer group"
                  data-testid={`biotech-asset-${a.id}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleAsset(a.id)}
                    className="mt-0.5 h-3 w-3 rounded border-slate-300"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-slate-800 truncate group-hover:text-slate-900">
                      {a.asset_name || '(unnamed)'}
                      {a.stage && <span className="ml-1.5 text-[9px] font-normal text-slate-500">· {a.stage}</span>}
                    </p>
                    {a.indication && <p className="text-[10px] text-slate-500 truncate">{a.indication}</p>}
                    {aiNote && (
                      <p className="text-[10px] text-violet-600 italic mt-0.5 line-clamp-2" data-testid={`scope-rationale-${a.id}`} title={aiNote}>
                        <Sparkles className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{aiNote}
                      </p>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Campaign LIST
// ─────────────────────────────────────────────
function CampaignsList() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newFocusTas, setNewFocusTas] = useState([]);
  const [newTaInput, setNewTaInput] = useState('');
  const navigate = useNavigate();

  const addNewTa = () => {
    const t = newTaInput.trim();
    if (!t) return;
    if (newFocusTas.find(x => x.toLowerCase() === t.toLowerCase())) {
      setNewTaInput('');
      return;
    }
    setNewFocusTas(prev => [...prev, t]);
    setNewTaInput('');
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(r.data || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const createCampaign = async () => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    try {
      const r = await axios.post(`${API}/campaigns`, {
        name: newName,
        notes: newNotes,
        focus_therapeutic_areas: newFocusTas,
      }, { withCredentials: true });
      toast.success('Campaign created');
      setShowNew(false); setNewName(''); setNewNotes(''); setNewFocusTas([]); setNewTaInput('');
      navigate(`/campaigns/${r.data.id}`);
    } catch { toast.error('Failed'); }
  };

  const deleteFromList = async (cid, cname, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${cname}" and all its data?`)) return;
    try {
      await axios.delete(`${API}/campaigns/${cid}`, { withCredentials: true });
      toast.success('Deleted');
      fetchList();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4 max-w-6xl px-6 py-6" data-testid="campaigns-page">
      <div className="flex items-start justify-between gap-4 pt-1">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Campaigns</h1>
          <p className="text-xs text-slate-500 mt-0.5">Conference &amp; initiative-scoped biotech → pharma introductions</p>
        </div>
        <Button onClick={() => setShowNew(true)} size="sm" className="bg-slate-900 text-white gap-1.5 h-8 text-xs shrink-0" data-testid="new-campaign-btn">
          <Plus className="w-3.5 h-3.5" /> New Campaign
        </Button>
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}

      {!loading && campaigns.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center">
            <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-900">No campaigns yet</p>
            <p className="text-xs text-slate-500 mb-3">Create one for ASCO 2026, BIO, JPM, or any time-bound initiative.</p>
            <Button onClick={() => setShowNew(true)} size="sm" className="bg-slate-900 text-white text-xs h-8">Create first</Button>
          </CardContent>
        </Card>
      )}

      {!loading && campaigns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {campaigns.map(c => (
            <Card
              key={c.id}
              onClick={() => navigate(`/campaigns/${c.id}`)}
              className="cursor-pointer hover:shadow-md transition-shadow group"
              data-testid={`campaign-card-${c.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm font-bold text-slate-900 truncate flex-1">{c.name}</h3>
                  <button
                    onClick={(e) => deleteFromList(c.id, c.name, e)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all"
                    data-testid={`delete-campaign-${c.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                {c.notes && <p className="text-[10px] text-slate-500 mb-2 line-clamp-2">{c.notes}</p>}
                <div className="flex items-center gap-3 text-[10px] text-slate-600">
                  <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {c.biotech_count || 0} biotechs</span>
                  <span className="flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> {c.attendee_count || 0} pharmas</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="new-campaign-dialog">
          <DialogHeader><DialogTitle>New Campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Name</label>
              <Input placeholder="ASCO 2026" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-campaign-name" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Focus Therapeutic Areas <span className="text-slate-400 normal-case font-normal">(drives AI auto-scope)</span></label>
              <div className="flex flex-wrap gap-1 mt-1">
                {newFocusTas.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-[11px]" data-testid={`new-campaign-focus-ta-${t}`}>
                    {t}
                    <button onClick={() => setNewFocusTas(prev => prev.filter(x => x !== t))} className="hover:text-sky-900" aria-label={`Remove ${t}`}><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1 mt-1.5">
                <Input
                  placeholder="e.g. Oncology, Immunology — press Enter"
                  value={newTaInput}
                  onChange={(e) => setNewTaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewTa(); } }}
                  data-testid="new-campaign-focus-ta-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={addNewTa} disabled={!newTaInput.trim()} data-testid="new-campaign-focus-ta-add">Add</Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">When set, new biotechs added to this campaign auto-pre-select only assets whose indication or therapeutic area falls inside the listed TA(s) (AI-classified by Claude).</p>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Notes (optional)</label>
              <textarea rows={3} value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-sky-200"
                placeholder="Oncology conference, Chicago, May 29 – Jun 2 2026…" data-testid="new-campaign-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={createCampaign} className="bg-slate-900 text-white" data-testid="create-campaign-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────
//  Campaign DETAIL — attendee row (expandable)
// ─────────────────────────────────────────────
function AttendeeRow({ row, campaignId, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [ranking, setRanking] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [editableDraft, setEditableDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const [promotingId, setPromotingId] = useState(null);

  // Single-click pipeline: runs enrich -> rank -> draft for THIS row only,
  // using the same backend helper as the bulk flow. Resumable: re-clicking
  // after a partial completion picks up where it left off. Use the
  // "Re-prepare from scratch" mode (force_redraft) to ignore cached steps.
  const prepareOne = async (forceRedraft = false) => {
    if (forceRedraft && !window.confirm(
      `Re-run AI for ${row.company_name}?\n\n` +
      `This discards any cached enrichment / ranking / draft for this pharma and recomputes from scratch — usually only needed when the pharma's profile has changed.`
    )) return;
    setEnriching(true); setRanking(true); setDrafting(true);
    try {
      const r = await axios.post(
        `${API}/campaigns/${campaignId}/attendees/bulk-prepare`,
        { attendee_row_ids: [row.id], force_redraft: forceRedraft },
        { withCredentials: true, timeout: 300000 }
      );
      const drafted = (r.data?.drafts || [])[0];
      const skipped = (r.data?.skipped || [])[0];
      if (drafted) {
        toast.success(`${row.company_name} enriched, ranked, and drafted`);
      } else if (skipped) {
        toast.error(`${row.company_name}: ${skipped.skip_reason}`);
      } else {
        toast.warning('No result — try again');
      }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pipeline failed');
    } finally {
      setEnriching(false); setRanking(false); setDrafting(false);
    }
  };

  const openSavedDraft = () => {
    setEditableDraft(row.draft);
    setShowDraft(true);
  };

  const sendTest = async () => {
    if (!editableDraft) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/campaigns/${campaignId}/attendees/${row.id}/send-test`, {
        subject: editableDraft.subject, body: editableDraft.body,
      }, { withCredentials: true });
      toast.success(`Test sent to ${r.data.sent_to}`);
      setShowDraft(false);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const promote = async (contactId) => {
    setPromotingId(contactId);
    try {
      const r = await axios.post(`${API}/campaigns/${campaignId}/attendees/${row.id}/promote/${contactId}`, {}, { withCredentials: true });
      toast.success(r.data.duplicate ? 'Already in Pharma Contacts' : 'Added to Pharma Contacts');
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Promote failed'); }
    setPromotingId(null);
  };

  const remove = async () => {
    if (!window.confirm('Remove this pharma from campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/attendees/${row.id}`, { withCredentials: true });
      onChange();
    } catch { toast.error('Failed'); }
  };

  const hasEnrich = !!row.enrichment;
  const hasRank = !!row.ranked_assets && row.ranked_assets.length > 0;
  const hasDraft = !!row.draft;
  const attendees = row.attendees || [];

  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={`attendee-row-${row.id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={() => setExpanded(x => !x)} className="text-slate-400 hover:text-slate-700">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <div className={`w-1.5 h-1.5 rounded-full ${row.pharma_id ? 'bg-emerald-500' : 'bg-slate-300'}`} title={row.pharma_id ? 'Matched to profile' : 'New'} />
        <p className="text-sm font-semibold text-slate-900 flex-1 truncate">{row.company_name}</p>
        <span className="text-[10px] text-slate-500">{attendees.length} attendee{attendees.length !== 1 ? 's' : ''}</span>
        {/* Status pills */}
        <div className="flex items-center gap-1">
          {hasRank && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-violet-50 text-violet-700 rounded">RANKED</span>}
          {hasDraft && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">DRAFTED</span>}
        </div>
        <button onClick={remove} className="text-slate-400 hover:text-red-500 p-1" data-testid={`remove-attendee-${row.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 px-3 py-3 space-y-3 bg-slate-50/40">
          {/* Attendees list */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Attendees</p>
            {attendees.length === 0 && <p className="text-xs text-slate-400">No attendees listed — only the company name. Add attendees by re-uploading with contact details.</p>}
            {attendees.length > 0 && (
              <div className="space-y-1">
                {attendees.map(a => (
                  <div key={a.id} className="flex items-center gap-2 text-xs py-1">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-slate-800">{a.name || '(no name)'}</span>
                      {a.title && <span className="text-slate-500"> · {a.title}</span>}
                      {a.email && <span className="text-slate-500"> · {a.email}</span>}
                    </div>
                    {a.promoted ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
                        <CheckCircle2 className="w-3 h-3" /> In Pharma Contacts
                      </span>
                    ) : (
                      <Button
                        onClick={() => promote(a.id)}
                        disabled={!a.email || promotingId === a.id}
                        size="sm" variant="ghost"
                        className="h-6 text-[10px] gap-1 text-slate-600 hover:bg-slate-100"
                        title={a.email ? 'Add to main Pharma Contacts' : 'Needs email to promote'}
                        data-testid={`promote-${a.id}`}
                      >
                        {promotingId === a.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <UserPlus className="w-2.5 h-2.5" />} Promote
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Enrichment summary */}
          {hasEnrich && (
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">AI Pharma Profile</p>
              <div className="text-xs text-slate-700 space-y-1 bg-white rounded border border-slate-200 p-2">
                {row.enrichment.therapeutic_focus?.length > 0 && <p><span className="font-semibold">Focus:</span> {row.enrichment.therapeutic_focus.join(', ')}</p>}
                {row.enrichment.preferred_stages?.length > 0 && <p><span className="font-semibold">Stages:</span> {row.enrichment.preferred_stages.join(', ')}</p>}
                {row.enrichment.licensing_priorities && <p className="text-slate-600">{row.enrichment.licensing_priorities}</p>}
                {row.enrichment.recent_deals_summary && <p className="text-slate-600 italic">{row.enrichment.recent_deals_summary}</p>}
              </div>
            </div>
          )}

          {/* Ranked assets */}
          {hasRank && (
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Recommended Assets to Pitch</p>
              <div className="space-y-1">
                {row.ranked_assets.map(a => (
                  <div key={a.asset_id} className="flex items-start gap-2 text-xs bg-white rounded border border-slate-200 p-2">
                    <div className="text-violet-700 font-bold text-sm w-5 shrink-0">#{a.rank}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-semibold text-slate-900">{a.asset_name}</p>
                        <span className="text-[9px] font-bold px-1 bg-violet-50 text-violet-700 rounded">Fit {a.fit_score}</span>
                      </div>
                      <p className="text-[11px] text-slate-500">{a.company_name} · {a.stage} · {a.indication}</p>
                      <p className="text-[11px] text-slate-700 mt-1">{a.rationale}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action bar — single "Enrich profile" button runs enrich -> rank -> draft
              in one go (May 2026 simplification). The pipeline is resumable, so
              re-clicking only redoes the missing steps. */}
          <div className="flex items-center gap-2 pt-1">
            {!hasDraft ? (
              <Button
                onClick={() => prepareOne(false)}
                disabled={enriching || ranking || drafting}
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700 h-7 text-[11px] gap-1.5"
                data-testid={`prepare-${row.id}`}
                title="Run Perplexity enrichment + asset ranking + Claude draft for this pharma in one go"
              >
                {(enriching || ranking || drafting)
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> {enriching ? 'Enriching…' : ranking ? 'Ranking…' : 'Drafting…'}</>
                  : <><Sparkles className="w-3 h-3" /> Enrich profile</>}
              </Button>
            ) : (
              <>
                <Button
                  onClick={openSavedDraft}
                  size="sm"
                  className="bg-slate-900 text-white hover:bg-slate-800 h-7 text-[11px] gap-1.5"
                  data-testid={`open-draft-${row.id}`}
                >
                  <Send className="w-3 h-3" /> Open draft
                </Button>
                <Button
                  onClick={() => prepareOne(true)}
                  disabled={enriching || ranking || drafting}
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] gap-1.5"
                  data-testid={`reprepare-${row.id}`}
                  title="Re-run the full pipeline from scratch (discards cached enrichment / ranking / draft)"
                >
                  {(enriching || ranking || drafting)
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Globe className="w-3 h-3" />}
                  Re-enrich profile
                </Button>
              </>
            )}
          </div>

          <p className="text-[10px] text-slate-400 italic">
            One click → Perplexity enriches the pharma's BD profile → Claude ranks our in-scope assets against their priorities → personalised draft ready to review &amp; send.
          </p>
        </div>
      )}

      {/* Draft Email Dialog */}
      <Dialog open={showDraft} onOpenChange={setShowDraft}>
        <DialogContent className="max-w-2xl" data-testid="draft-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email to {row.company_name}</DialogTitle>
          </DialogHeader>
          {editableDraft && (
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Subject</label>
                <Input value={editableDraft.subject} onChange={(e) => setEditableDraft(d => ({ ...d, subject: e.target.value }))} data-testid="draft-subject" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Body</label>
                <textarea
                  rows={16}
                  value={editableDraft.body}
                  onChange={(e) => setEditableDraft(d => ({ ...d, body: e.target.value }))}
                  className="w-full text-xs font-mono border border-slate-200 rounded p-3 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="draft-body"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                <span className="font-bold">Test flow.</span> "Send test to me" delivers this to your own inbox (prefixed [TEST — for {row.company_name}]). Review, copy, send the real one outside the platform.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDraft(false)}>Close</Button>
            <Button onClick={sendTest} disabled={!editableDraft || sending} className="bg-slate-900 text-white gap-2" data-testid="send-test-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send test to me
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [camp, setCamp] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'contacts'

  const [showAddBiotech, setShowAddBiotech] = useState(false);
  const [btSearchQ, setBtSearchQ] = useState('');
  const [btSearchResults, setBtSearchResults] = useState([]);
  const [pickingBiotech, setPickingBiotech] = useState(null); // { id, company_name, therapeutic_area }
  const [pickingAssets, setPickingAssets] = useState([]); // loaded assets
  const [pickingAssetsLoading, setPickingAssetsLoading] = useState(false);
  const [pickedAssetIds, setPickedAssetIds] = useState(new Set());
  const [adding, setAdding] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadText, setUploadText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'paste'

  const [contactFilter, setContactFilter] = useState('all'); // 'all' | 'pending' | 'promoted' | 'responded'
  const [promotingId, setPromotingId] = useState(null);

  // Bulk biotech outreach state is no longer exposed in UI (May 2026 user
  // pivot: emails go to pharmas, not biotechs). Backend endpoints remain
  // available at /campaigns/{id}/biotechs/draft-emails for scripted use.

  // Bulk pharma enrichment — Perplexity-only pass (no rank/draft). Useful
  // for inspecting what we learned about each pharma before spending Claude
  // budget on drafting.
  const [bulkEnrichLoading, setBulkEnrichLoading] = useState(false);
  const enrichAllPharmas = async (force = false) => {
    if (force && !window.confirm(
      'Re-enrich every pharma from scratch? This ignores cached enrichment and re-runs Perplexity searches — usually only needed when the pharma list changed.'
    )) return;
    setBulkEnrichLoading(true);
    try {
      const r = await axios.post(
        `${API}/campaigns/${id}/attendees/bulk-enrich`,
        { force },
        { withCredentials: true, timeout: 600000 }
      );
      const enriched = r.data?.enriched?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      if (enriched === 0 && skipped > 0) {
        toast.error(`All ${skipped} pharma(s) failed enrichment — check console`);
      } else {
        toast.success(`Enriched ${enriched} pharma${enriched === 1 ? '' : 's'}${skipped ? ` · ${skipped} failed` : ''}`, { duration: 5000 });
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk enrich failed');
    } finally {
      setBulkEnrichLoading(false);
    }
  };

  // Bulk pharma outreach (May 2026) — Enrich+Rank+Draft for all → Review → Send
  // The narrative: ForesightRx represents the campaign's biotechs/assets; this
  // bulk flow pitches each pharma attendee on the assets best-matched to their
  // priorities (Perplexity-enriched), then sends or test-sends in parallel.
  const [bulkPharmaDialog, setBulkPharmaDialog] = useState({ open: false, drafts: [], skipped: [], total: 0 });
  const [bulkPharmaLoading, setBulkPharmaLoading] = useState(false);
  const [bulkPharmaSending, setBulkPharmaSending] = useState(false);
  // Live progress while the background bulk-send task runs.
  // null when idle; {total, sent, failed, already_sent} while polling.
  const [bulkPharmaProgress, setBulkPharmaProgress] = useState(null);
  const [bulkPharmaForce, setBulkPharmaForce] = useState(false);
  const [bulkPharmaLastTest, setBulkPharmaLastTest] = useState(null);  // {sent, when, pharma_count} — set after successful test send
  const [bulkPharmaLastSend, setBulkPharmaLastSend] = useState(null);  // {sent, failed, when, pharma_count} — set after successful REAL send
  // Recovery modal for legacy drift (one-time): user pastes emails already sent
  const [markSentDialog, setMarkSentDialog] = useState({ open: false, value: '', submitting: false });
  // Per-recipient "already sent" map keyed by attendee_row_id.
  // {[row_id]: {company_name, sent_emails: [{to_email, to_name, sent_at}], last_sent_at}}
  const [sentMap, setSentMap] = useState({});
  const [sentTotal, setSentTotal] = useState(0);

  const prepareBulkPharmaEmails = async (force = false) => {
    if (!(camp?.biotechs || []).length) {
      toast.error('Add at least one Confirmed Biotech in Section 1 first — the email pitches their assets.');
      return;
    }
    if (force && !window.confirm(
      'Re-run AI for every pharma? This ignores any cached enrichment/ranking/drafts and recomputes from scratch — takes 2-3× as long and uses fresh Perplexity + Claude calls.'
    )) return;
    setBulkPharmaLoading(true);
    setBulkPharmaDialog({ open: true, drafts: [], skipped: [], total: 0 });
    try {
      const r = await axios.post(
        `${API}/campaigns/${id}/attendees/bulk-prepare`,
        { force_redraft: force },
        { withCredentials: true, timeout: 600000 }   // 10-min ceiling: 50 pharmas × ~6s each
      );
      const drafts = r.data?.drafts || [];
      setBulkPharmaDialog({
        open: true,
        drafts,
        skipped: r.data?.skipped || [],
        total: r.data?.total || 0,
      });
      if (drafts.length === 0) {
        toast.warning(`No drafts generated · ${r.data?.skipped?.length || 0} pharma(s) skipped. See dialog for reasons.`);
      } else {
        toast.success(`${drafts.length} pharma draft${drafts.length === 1 ? '' : 's'} ready for review`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk prepare failed');
      setBulkPharmaDialog({ open: false, drafts: [], skipped: [], total: 0 });
    } finally {
      setBulkPharmaLoading(false);
    }
  };

  const updateBulkPharmaDraft = (idx, patch) => {
    setBulkPharmaDialog(prev => ({
      ...prev,
      drafts: prev.drafts.map((d, i) => i === idx ? { ...d, ...patch } : d),
    }));
  };

  // Unified bulk send. `mode` is 'test' or 'real':
  //  - 'test' → first 3 included pharmas, 1 recipient each, [TEST → pharma]
  //    prefix to user's inbox. Nothing logged.
  //  - 'real' → all included pharmas. Backend dedups vs outreach log so
  //    re-clicks can't produce duplicates.
  // Removes the old "Send as TEST" checkbox state machine — caller just
  // picks which button to click.
  const sendBulkPharmaEmails = async (mode = 'real') => {
    const isTest = mode === 'test';
    const included = bulkPharmaDialog.drafts.filter(d => d.approved !== false && (d.recipients || []).length > 0);
    if (included.length === 0) {
      toast.error('Nothing to send. Every row is either excluded or has no recipients.');
      return;
    }
    // TEST: take just the first 3 included pharmas, slice recipients to 1
    // each (so the user gets exactly 3 sample emails — one per pharma).
    // REAL: pass every included pharma with its full recipient list.
    const approved = isTest
      ? included.slice(0, 3).map(d => ({
          ...d,
          recipients: (d.recipients || []).slice(0, 1),
        })).filter(d => d.recipients.length > 0)
      : included;
    if (approved.length === 0) {
      toast.error('No recipients available.');
      return;
    }
    const recipientCount = approved.reduce((n, d) => n + (d.recipients?.length || 0), 0);
    if (!isTest && !window.confirm(
      `Send real emails to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'} now ` +
      `(across ${approved.length} pharma${approved.length === 1 ? '' : 's'})?\n\n` +
      `Each attendee receives a personalised "Dear [Name]," email in parallel via Gmail. ` +
      `Anyone who already received an email for this campaign is auto-skipped by the backend — no duplicates possible.`
    )) return;
    setBulkPharmaSending(true);
    try {
      const r = await axios.post(
        `${API}/campaigns/${id}/attendees/bulk-send`,
        { drafts: approved, test_mode: isTest },
        { withCredentials: true, timeout: 60000 }  // request returns instantly with task_id
      );
      // Background-task contract (mirrors AI Mapping):
      // - r.data.task_id !== null → poll status endpoint every 2s
      // - r.data.task_id === null → synchronous shortcut response
      //   (e.g. every recipient was already_sent → status: "completed")
      const taskId = r.data?.task_id;
      const initial = r.data || {};
      if (!taskId) {
        // Shortcut path — handle as a completed response right away
        await _applyBulkSendResult(initial, isTest, approved);
        return;
      }

      // Poll the status endpoint. The "Sending overlay" already shows during
      // bulkPharmaSending=true; we update the displayed counts via a state.
      setBulkPharmaProgress({ total: initial.total || 0, sent: 0, failed: 0, already_sent: initial.already_sent || 0 });
      const pollStart = Date.now();
      const maxPollDuration = 20 * 60 * 1000;  // 20 min hard cap — Gmail rate limits before then anyway
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise(res => setTimeout(res, 2000));
        if (Date.now() - pollStart > maxPollDuration) {
          toast.error('Send took longer than 20 minutes — check Gmail Sent folder for actual outcome', { duration: 12000 });
          break;
        }
        const sr = await axios.get(
          `${API}/campaigns/${id}/bulk-send-status/${taskId}`,
          { withCredentials: true, timeout: 30000 }
        );
        const d = sr.data || {};
        setBulkPharmaProgress({
          total: d.total || initial.total || 0,
          sent: d.sent || 0,
          failed: d.failed || 0,
          already_sent: d.already_sent || initial.already_sent || 0,
        });
        if (d.status === 'completed') {
          await _applyBulkSendResult({
            sent: d.sent || 0,
            failed: d.failed || 0,
            test_mode: d.test_mode,
            already_sent: d.already_sent || 0,
            results: d.results || [],
          }, isTest, approved);
          break;
        }
        if (d.status === 'failed') {
          toast.error(d.error || 'Background send failed — check the outreach log for partial results', { duration: 12000 });
          break;
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = detail
        || (status ? `Send failed (HTTP ${status}) — check Gmail connection / network and retry` : '')
        || (err?.code === 'ECONNABORTED' ? 'Send timed out after 5 min — Gmail may be rate-limited; try smaller batches' : '')
        || err?.message
        || 'Send failed';
      toast.error(msg, { duration: 8000 });
      console.error('[bulk-send error]', { status, detail, err });
    } finally {
      setBulkPharmaSending(false);
      setBulkPharmaProgress(null);
    }
  };

  // Apply the final result from either the shortcut response or the polled
  // completion. Shared by both code paths to avoid drift between them.
  const _applyBulkSendResult = async (data, isTest, approved) => {
    const { sent, failed, test_mode: serverIsTest, already_sent } = data;
    if (isTest || serverIsTest) {
      toast.success(`${sent} test email${sent === 1 ? '' : 's'} sent to your inbox · ${failed} failed`, { duration: 5000 });
      const when = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setBulkPharmaLastTest({ sent, when, pharma_count: approved.length });
    } else {
      const dupSkip = already_sent || 0;
      const toastMsg = dupSkip > 0
        ? `${sent} sent · ${failed} failed · ${dupSkip} skipped (already emailed earlier — no duplicates dispatched)`
        : `${sent} email${sent === 1 ? '' : 's'} sent · ${failed} failed`;
      toast.success(toastMsg, { duration: 8000 });
      const when = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setBulkPharmaLastSend({ sent, failed, when, pharma_count: approved.length, already_sent: dupSkip });
      setBulkPharmaLastTest(null);
      fetchAll();
    }
  };

  // Opens the bulk review dialog populated with already-drafted pharmas —
  // built ENTIRELY from local `attendees` state. NO backend call, NO AI re-runs,
  // NO proxy timeout risk. The drafts are already loaded with the page; we just
  // shape them into the dialog's expected schema and send via `/bulk-send`
  // (which is fast — Gmail dispatch only, no LLM).
  const openSendAllDialogLocal = () => {
    const drafted = (attendees || []).filter(a => {
      const d = a?.draft || {};
      return (d.subject || '').trim() && (d.body || '').trim();
    });
    if (drafted.length === 0) {
      toast.error('No drafts yet. Click "Draft emails for all" first.');
      return;
    }
    setBulkPharmaLastTest(null);   // fresh dialog session — clear any prior test confirmation
    setBulkPharmaLastSend(null);   // …and any prior real-send confirmation
    const drafts = drafted.map(a => {
      const recipients = (a.attendees || [])
        .map(x => ({ name: (x.name || '').trim(), email: (x.email || '').trim() }))
        .filter(x => x.email);
      const top = (a.ranked_assets || []).slice(0, 3);
      const rank_summary = top
        .map((x, i) => `#${i + 1} ${x.asset_name || ''} (${x.company_name || ''})`)
        .join(' · ');
      return {
        attendee_row_id: a.id,
        company_name: a.company_name,
        recipients,
        subject: a.draft.subject,
        body: a.draft.body,
        rank_summary,
        approved: true,
      };
    });
    const skipped = (attendees || [])
      .filter(a => !drafted.includes(a))
      .map(a => ({ company_name: a.company_name, skip_reason: 'No draft yet — click "Draft emails for all" to generate one' }));
    setBulkPharmaDialog({
      open: true,
      drafts,
      skipped,
      total: drafts.length + skipped.length,
    });
    toast.success(`${drafts.length} draft${drafts.length === 1 ? '' : 's'} ready to review and send`);

    // ─── Silent Gmail backfill ──────────────────────────────────────────────
    // The user shouldn't have to think about audit-log drift. Trigger
    // sync-sent-from-gmail in the background so any pre-durability sends
    // (where Gmail dispatched but DB didn't log) get recovered automatically.
    // - On success with recoveries: refresh attendees/sentMap so per-row
    //   "Already emailed" pills appear and the remaining-count drops.
    // - On scope error: show a one-time persistent toast with a clear fix.
    // - On any other failure: silently skip (the durable dispatch handles
    //   future drift, so worst case the user sees a slightly stale count).
    (async () => {
      try {
        const r = await axios.post(
          `${API}/campaigns/${id}/sync-sent-from-gmail`,
          {},
          { withCredentials: true, timeout: 120000 }
        );
        const { recovered, already_logged, scanned } = r.data;
        if (recovered > 0) {
          toast.success(
            `Auto-recovered ${recovered} previously-sent email${recovered === 1 ? '' : 's'} from your Gmail Sent folder · ` +
            `the "remaining recipients" count below has been updated automatically.`,
            { duration: 9000 }
          );
          fetchAll();
        }
        // recovered=0 + already_logged>0 → already in sync, stay silent
        // recovered=0 + already_logged=0 + scanned>0 → nothing matched (no campaign emails in Sent), stay silent
        console.log('[gmail auto-sync]', { recovered, already_logged, scanned });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403) {
          toast.warning(
            <div>
              <strong>Gmail readonly access not granted yet.</strong>
              <br />Disconnect & reconnect Gmail in Settings → Integrations so the system can auto-detect already-sent emails.
              <br /><span className="text-[10px] opacity-80">Until then, you can still send — the durable audit log prevents future drift, but earlier sends won't be auto-detected.</span>
            </div>,
            { duration: 12000 }
          );
        } else if (status === 400 && (detail || '').includes('Gmail not connected')) {
          // User hasn't connected Gmail at all — stay quiet; sending will fail with the same message
        } else {
          // Other failure — log but don't pollute the UI
          console.warn('[gmail auto-sync failed]', { status, detail, err });
        }
      }
    })();
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/campaigns/${id}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${id}/attendees`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${id}/sent-recipients`, { withCredentials: true }).catch(() => ({ data: { by_row: {}, total_sends: 0 } })),
      ]);
      setCamp(r1.data);
      setAttendees(r2.data || []);
      setSentMap(r3.data?.by_row || {});
      setSentTotal(r3.data?.total_sends || 0);
    } catch { toast.error('Campaign not found'); navigate('/campaigns'); }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, [id]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (btSearchQ.trim().length < 2) { setBtSearchResults([]); return; }
      try {
        const r = await axios.get(`${API}/campaigns/biotech-search?q=${encodeURIComponent(btSearchQ)}`, { withCredentials: true });
        setBtSearchResults(r.data || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [btSearchQ]);

  // Step 1 — user clicked a search result → load its assets so they can pick which to pitch
  const pickBiotech = async (bt) => {
    setPickingBiotech(bt);
    setPickingAssetsLoading(true);
    setPickingAssets([]);
    setPickedAssetIds(new Set());
    try {
      const r = await axios.get(`${API}/campaigns/biotech-assets/${bt.id}`, { withCredentials: true });
      const list = r.data || [];
      setPickingAssets(list);
      // Default: pre-select all assets
      setPickedAssetIds(new Set(list.map(a => a.id)));
    } catch { toast.error('Failed to load assets'); setPickingBiotech(null); }
    setPickingAssetsLoading(false);
  };

  const toggleAssetPick = (aid) => {
    setPickedAssetIds(prev => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  };

  // Step 2 — user clicked Add → commit biotech + selected assets
  const confirmAddBiotech = async () => {
    if (!pickingBiotech) return;
    if (pickedAssetIds.size === 0) {
      toast.error('Pick at least one asset');
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API}/campaigns/${id}/biotechs`,
        { biotech_id: pickingBiotech.id, asset_ids: Array.from(pickedAssetIds) },
        { withCredentials: true }
      );
      toast.success(`${pickingBiotech.company_name} added with ${pickedAssetIds.size} asset${pickedAssetIds.size !== 1 ? 's' : ''}`);
      // Reset dialog state
      setShowAddBiotech(false);
      setBtSearchQ(''); setBtSearchResults([]);
      setPickingBiotech(null); setPickingAssets([]); setPickedAssetIds(new Set());
      fetchAll();
    } catch { toast.error('Failed'); }
    setAdding(false);
  };

  const removeBiotech = async (btId) => {
    if (!window.confirm('Remove from campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}/biotechs/${btId}`, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const saveBiotechAssets = async (btId, assetIds) => {
    try {
      const res = await axios.put(`${API}/campaigns/${id}/biotechs/${btId}/assets`, { asset_ids: assetIds }, { withCredentials: true });
      toast.success(`${res.data.selected_count} asset${res.data.selected_count !== 1 ? 's' : ''} selected`);
      fetchAll();
    } catch { toast.error('Failed to save assets'); }
  };

  const uploadAttendeesText = async () => {
    if (!uploadText.trim()) return;
    setUploading(true);
    try {
      const r = await axios.post(`${API}/campaigns/${id}/attendees/upload`, { text: uploadText }, { withCredentials: true });
      toast.success(`Added ${r.data.added}${r.data.merged ? ' · merged ' + r.data.merged : ''}${r.data.matched_profiles ? ' · ' + r.data.matched_profiles + ' matched' : ''}`);
      setShowUpload(false); setUploadText('');
      fetchAll();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const uploadAttendeesFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/campaigns/${id}/attendees/upload-file`, fd, { withCredentials: true });
      toast.success(`Added ${r.data.added}${r.data.merged ? ' · merged ' + r.data.merged : ''}${r.data.matched_profiles ? ' · ' + r.data.matched_profiles + ' matched' : ''}`);
      setShowUpload(false);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const promoteContact = async (rowId, contactId) => {
    setPromotingId(contactId);
    try {
      const r = await axios.post(`${API}/campaigns/${id}/attendees/${rowId}/promote/${contactId}`, {}, { withCredentials: true });
      toast.success(r.data.duplicate ? 'Already in Pharma Contacts' : 'Added to Pharma Contacts');
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setPromotingId(null);
  };

  const toggleResponded = async (rowId, contactId) => {
    try {
      await axios.post(`${API}/campaigns/${id}/attendees/${rowId}/toggle-responded/${contactId}`, {}, { withCredentials: true });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all pharma attendees?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}/attendees`, { withCredentials: true });
      fetchAll();
    } catch {}
  };

  const deleteCampaign = async () => {
    if (!window.confirm(`Delete "${camp.name}" and all its data?`)) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success('Deleted');
      navigate('/campaigns');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!camp) return null;

  // Flatten all attendees across all pharma rows
  const allContacts = [];
  for (const row of attendees) {
    for (const a of (row.attendees || [])) {
      allContacts.push({ ...a, pharma_name: row.company_name, row_id: row.id });
    }
  }
  const filteredContacts = allContacts.filter(c => {
    if (contactFilter === 'pending') return !c.promoted;
    if (contactFilter === 'promoted') return c.promoted;
    if (contactFilter === 'responded') return c.responded;
    return true;
  });

  return (
    <div className="space-y-4 max-w-6xl px-6 py-6" data-testid="campaign-detail">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pt-1">
        <div className="min-w-0 flex-1">
          <Link to="/campaigns" className="text-xs text-slate-500 hover:text-slate-700">← All campaigns</Link>
          <h1 className="text-xl font-bold text-slate-900 mt-0.5 truncate">{camp.name}</h1>
          {camp.notes && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{camp.notes}</p>}
          <CampaignFocusTasEditor
            campaignId={camp.id}
            initial={camp.focus_therapeutic_areas || []}
            onSaved={(tas) => setCamp(prev => ({ ...prev, focus_therapeutic_areas: tas }))}
          />
          <CampaignContextEditor
            campaignId={camp.id}
            initial={camp.campaign_context || ''}
            onSaved={(ctx) => setCamp(prev => ({ ...prev, campaign_context: ctx }))}
          />
        </div>
        <Button variant="ghost" size="sm" onClick={deleteCampaign} className="text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0" data-testid="delete-campaign">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${activeTab === 'overview' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          data-testid="tab-overview">
          Overview
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${activeTab === 'contacts' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          data-testid="tab-contacts">
          All Attendees
          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full font-bold">{allContacts.length}</span>
        </button>
      </div>

      {activeTab === 'overview' && <>
        {/* Section 1 — Confirmed Biotechs */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-900">Confirmed Biotech Assets <span className="text-slate-400 font-normal ml-1">· {camp.biotechs?.length || 0}</span></h2>
              <Button onClick={() => setShowAddBiotech(true)} size="sm" variant="outline" className="gap-1.5 h-7 text-xs" data-testid="add-biotech-btn">
                <Plus className="w-3 h-3" /> Add
              </Button>
            </div>
            {(camp.biotechs || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No biotechs yet. Add ones that responded positively to your outreach.</p>
            ) : (
              <div className="space-y-2">
                {camp.biotechs.map(cb => (
                  <BiotechCampaignCard
                    key={cb.id}
                    cb={cb}
                    campaignId={camp.id}
                    focusTas={camp.focus_therapeutic_areas || []}
                    onRemove={() => removeBiotech(cb.biotech_id)}
                    onSaveAssetSelection={(assetIds) => saveBiotechAssets(cb.biotech_id, assetIds)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2 — Pharma Attendees */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-slate-900">Pharma Attendees <span className="text-slate-400 font-normal ml-1">· {attendees.length}</span></h2>
                <p className="text-[10px] text-slate-500">Per-pharma AI enrichment → asset ranking → customized draft email</p>
              </div>
              <div className="flex items-center gap-1">
                {attendees.length > 0 && (
                  <>
                    <Button
                      onClick={() => prepareBulkPharmaEmails(false)}
                      disabled={bulkPharmaLoading || bulkPharmaSending}
                      size="sm"
                      variant="outline"
                      className="gap-1.5 h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="bulk-prepare-pharmas-btn"
                      title="Runs the AI pipeline (Perplexity enrichment → Claude ranking → Claude draft) for every pharma attendee. Cached rows are reused as-is; only fresh rows hit the LLM."
                    >
                      {bulkPharmaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      Draft emails for all
                    </Button>
                    <Button
                      onClick={openSendAllDialogLocal}
                      disabled={bulkPharmaLoading || bulkPharmaSending}
                      size="sm"
                      variant="outline"
                      className="gap-1.5 h-7 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                      data-testid="bulk-send-all-pharmas-btn"
                      title="Opens the review dialog with every saved draft — built entirely from local data, NO backend call, NO AI re-runs. Edit / approve / send."
                    >
                      <Send className="w-3 h-3" />
                      Send mail to all
                    </Button>
                    {sentTotal > 0 && (
                      <span
                        className="text-[10px] font-semibold px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-800 inline-flex items-center gap-1 ml-1"
                        title={`${sentTotal} email${sentTotal === 1 ? '' : 's'} already dispatched for this campaign (across ${Object.keys(sentMap).length} pharma${Object.keys(sentMap).length === 1 ? '' : 's'}). Re-clicking Send mail to all auto-skips them — no duplicates.`}
                        data-testid="campaign-sent-total-badge"
                      >
                        ✓ {sentTotal} already sent
                      </span>
                    )}
                    <Button
                      onClick={async () => {
                        if (!window.confirm(
                          'Consolidate duplicate rows?\n\n' +
                          'Merges variants of the same pharma (e.g. "AbbVie" + "AbbVie Inc" + ' +
                          '"ABBVIE BIOPHARMACEUTICALS") into a single row, using the canonical ' +
                          'name from your pharma CRM. Attendees inside each duplicate get ' +
                          'merged and deduped by email.\n\nSafe to re-run.'
                        )) return;
                        try {
                          const r = await axios.post(
                            `${API}/campaigns/${id}/attendees/consolidate-duplicates`,
                            {},
                            { withCredentials: true }
                          );
                          const { consolidated_groups, rows_merged_away, total_rows_after } = r.data;
                          if (rows_merged_away === 0) {
                            toast.success(`No duplicates found — all ${total_rows_after} rows already canonical.`);
                          } else {
                            toast.success(
                              `Consolidated ${consolidated_groups} group${consolidated_groups === 1 ? '' : 's'}, ` +
                              `merged ${rows_merged_away} duplicate row${rows_merged_away === 1 ? '' : 's'}. ` +
                              `${total_rows_after} unique pharmas remain.`,
                              { duration: 6000 }
                            );
                          }
                          fetchAll();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Consolidation failed');
                        }
                      }}
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[10px] text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
                      data-testid="consolidate-attendees-btn"
                      title="Merge duplicate pharma rows (e.g. AbbVie + AbbVie Inc → one row). Uses your pharma CRM canonical names."
                    >
                      ◆ Consolidate
                    </Button>
                    <Button onClick={clearAll} size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400 hover:text-red-600">Clear all</Button>
                  </>
                )}
                <Button onClick={() => setShowUpload(true)} size="sm" variant="outline" className="gap-1.5 h-7 text-xs" data-testid="upload-attendees-btn">
                  <Plus className="w-3 h-3" /> Upload list
                </Button>
              </div>
            </div>

            {attendees.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-slate-200 rounded">
                <p className="text-xs text-slate-400 mb-1">Upload an Excel/CSV of pharmas attending the conference.</p>
                <p className="text-[10px] text-slate-500">Columns: Company | Name | Email | Title</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attendees.map(row => (
                  <AttendeeRow key={row.id} row={row} campaignId={id} onChange={fetchAll} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </>}

      {activeTab === 'contacts' && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-bold text-slate-900">All Attendee Contacts <span className="text-slate-400 font-normal ml-1">· {filteredContacts.length}</span></h2>
                <p className="text-[10px] text-slate-500">Contacts stay in this campaign. Promote individuals to your main Pharma Contacts DB once they respond.</p>
              </div>
              <div className="flex items-center gap-1">
                {['all', 'pending', 'responded', 'promoted'].map(f => (
                  <button
                    key={f}
                    onClick={() => setContactFilter(f)}
                    className={`px-2 py-1 text-[10px] rounded capitalize transition-colors ${contactFilter === f ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    data-testid={`filter-${f}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            {filteredContacts.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No contacts {contactFilter !== 'all' ? `(${contactFilter})` : ''}.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-semibold py-2 px-2">Pharma</th>
                      <th className="text-left font-semibold py-2 px-2">Name</th>
                      <th className="text-left font-semibold py-2 px-2">Title</th>
                      <th className="text-left font-semibold py-2 px-2">Email</th>
                      <th className="text-center font-semibold py-2 px-2">Responded</th>
                      <th className="text-right font-semibold py-2 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                        <td className="py-2 px-2 font-semibold text-slate-800">{c.pharma_name}</td>
                        <td className="py-2 px-2 text-slate-700">{c.name || '—'}</td>
                        <td className="py-2 px-2 text-slate-500">{c.title || '—'}</td>
                        <td className="py-2 px-2 text-slate-500">{c.email || '—'}</td>
                        <td className="py-2 px-2 text-center">
                          <input
                            type="checkbox"
                            checked={!!c.responded}
                            onChange={() => toggleResponded(c.row_id, c.id)}
                            className="w-3.5 h-3.5 accent-emerald-600"
                            data-testid={`responded-${c.id}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-right">
                          {c.promoted ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                              <CheckCircle2 className="w-3 h-3" /> Promoted
                            </span>
                          ) : (
                            <Button
                              onClick={() => promoteContact(c.row_id, c.id)}
                              disabled={!c.email || promotingId === c.id}
                              size="sm" variant="outline"
                              className="h-6 text-[10px] gap-1"
                              title={!c.email ? 'Needs email' : 'Add to Pharma Contacts'}
                              data-testid={`promote-contact-${c.id}`}
                            >
                              {promotingId === c.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <UserPlus className="w-2.5 h-2.5" />}
                              Promote
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Biotech Asset Dialog — 2 steps: search biotech → pick assets → add */}
      <Dialog open={showAddBiotech} onOpenChange={(v) => {
        setShowAddBiotech(v);
        if (!v) {
          setBtSearchQ(''); setBtSearchResults([]);
          setPickingBiotech(null); setPickingAssets([]); setPickedAssetIds(new Set());
        }
      }}>
        <DialogContent className="max-w-lg" data-testid="add-biotech-dialog">
          <DialogHeader>
            <DialogTitle>
              {pickingBiotech
                ? `Select assets from ${pickingBiotech.company_name}`
                : 'Add a Biotech Asset to Campaign'}
            </DialogTitle>
          </DialogHeader>

          {!pickingBiotech ? (
            <>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <Input autoFocus placeholder="Search biotech companies..." value={btSearchQ}
                  onChange={(e) => setBtSearchQ(e.target.value)} className="pl-9" data-testid="biotech-search-input" />
              </div>
              <div className="max-h-80 overflow-y-auto -mx-6 px-6">
                {btSearchResults.map(bt => (
                  <button key={bt.id} onClick={() => pickBiotech(bt)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded" data-testid={`search-result-${bt.id}`}>
                    <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{bt.company_name}</p>
                      <p className="text-xs text-slate-500 truncate">{bt.therapeutic_area}</p>
                      {bt.match_reason && (
                        <p className="text-[10px] text-violet-600 italic truncate" data-testid={`search-match-reason-${bt.id}`}>matched on {bt.match_reason}</p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </button>
                ))}
                {btSearchQ.length >= 2 && btSearchResults.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No matches</p>}
                {btSearchQ.length < 2 && <p className="text-xs text-slate-400 text-center py-6">Type at least 2 characters</p>}
              </div>
            </>
          ) : (
            <>
              {/* Step 2 — asset picker */}
              <button
                onClick={() => { setPickingBiotech(null); setPickingAssets([]); setPickedAssetIds(new Set()); }}
                className="text-xs text-[#00709B] hover:underline flex items-center gap-1 -mb-1"
                data-testid="back-to-search">
                ← Back to search
              </button>
              <p className="text-[11px] text-slate-500">
                Pick the specific assets you want to pitch for this biotech. Only these will be used for smart-matching and outreach.
              </p>
              {pickingAssetsLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
              ) : pickingAssets.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">No assets found for this biotech in the database.</p>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-[10px]">
                    <button onClick={() => setPickedAssetIds(new Set(pickingAssets.map(a => a.id)))} className="text-[#00709B] hover:underline">Select all</button>
                    <button onClick={() => setPickedAssetIds(new Set())} className="text-slate-500 hover:underline">Select none</button>
                    <span className="ml-auto text-slate-400">{pickedAssetIds.size} of {pickingAssets.length} selected</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto -mx-6 px-6 divide-y divide-slate-100">
                    {pickingAssets.map(a => {
                      const checked = pickedAssetIds.has(a.id);
                      return (
                        <label key={a.id} className="flex items-start gap-2 py-2 cursor-pointer" data-testid={`picker-asset-${a.id}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleAssetPick(a.id)}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800">
                              {a.asset_name || '(unnamed)'}
                              {a.stage && <span className="ml-1.5 text-[10px] font-normal text-slate-500">· {a.stage}</span>}
                            </p>
                            {a.indication && <p className="text-[11px] text-slate-500">{a.indication}</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddBiotech(false)} disabled={adding}>Cancel</Button>
                <Button
                  onClick={confirmAddBiotech}
                  disabled={adding || pickedAssetIds.size === 0}
                  data-testid="confirm-add-biotech">
                  {adding ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Adding…</> : `Add ${pickedAssetIds.size || ''} asset${pickedAssetIds.size !== 1 ? 's' : ''}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Attendees Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg" data-testid="upload-attendees-dialog">
          <DialogHeader><DialogTitle>Upload Pharma Attendee List</DialogTitle></DialogHeader>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5 w-fit">
            <button
              onClick={() => setUploadMode('file')}
              className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${uploadMode === 'file' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="upload-mode-file">
              Excel / CSV file
            </button>
            <button
              onClick={() => setUploadMode('paste')}
              className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${uploadMode === 'paste' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid="upload-mode-paste">
              Paste text
            </button>
          </div>

          {uploadMode === 'file' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Upload an <code className="bg-slate-100 px-1 rounded">.xlsx</code> or <code className="bg-slate-100 px-1 rounded">.csv</code> file.
                First 4 columns: <span className="font-semibold">Company | Name | Email | Title</span>. Header row is auto-detected.
              </p>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-sky-400 transition-colors">
                <input
                  type="file"
                  id="attendee-file"
                  accept=".xlsx,.xlsm,.csv,.txt"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadAttendeesFile(f);
                    e.target.value = '';
                  }}
                  className="hidden"
                  data-testid="attendee-file-input"
                />
                <label htmlFor="attendee-file" className="cursor-pointer">
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                    </div>
                  ) : (
                    <>
                      <Plus className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <p className="text-xs font-semibold text-slate-700">Click to choose a file</p>
                      <p className="text-[10px] text-slate-400 mt-1">.xlsx, .xlsm, or .csv</p>
                    </>
                  )}
                </label>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-600">
                <p className="font-semibold mb-1">Expected columns:</p>
                <div className="grid grid-cols-4 gap-1 font-mono">
                  <div className="text-slate-500">Company</div>
                  <div className="text-slate-500">Name</div>
                  <div className="text-slate-500">Email</div>
                  <div className="text-slate-500">Title</div>
                  <div>Pfizer</div>
                  <div>Sarah Chen</div>
                  <div>sarah@pfizer.com</div>
                  <div>VP BD</div>
                </div>
              </div>
            </div>
          )}

          {uploadMode === 'paste' && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                One row per attendee. Separators: <code className="bg-slate-100 px-1 rounded">|</code>, <code className="bg-slate-100 px-1 rounded">,</code>, tab.
                Same company across multiple rows merges into one card.
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono text-slate-600 space-y-0.5">
                <p>Pfizer | Sarah Chen | sarah.chen@pfizer.com | VP BD</p>
                <p>Pfizer | Mark Lee | mark.lee@pfizer.com | Director</p>
                <p>Merck | Jane Doe | jane.doe@merck.com | SVP Licensing</p>
              </div>
              <textarea
                autoFocus rows={9} value={uploadText} onChange={(e) => setUploadText(e.target.value)}
                className="w-full text-xs font-mono border border-slate-200 rounded p-2 focus:outline-none focus:ring-2 focus:ring-sky-200"
                placeholder="Paste your attendee list here..."
                data-testid="attendees-textarea"
              />
              <DialogFooter className="pt-2">
                <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
                <Button onClick={uploadAttendeesText} disabled={!uploadText.trim() || uploading} className="bg-slate-900 text-white gap-2" data-testid="upload-attendees-submit">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Upload
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════
          Bulk pharma outreach — Prepare (enrich+rank+draft) → Review → Send
          Narrative: ForesightRx represents the campaign's Confirmed
          Biotechs / assets. This dialog drafts a personalised pitch to
          each pharma attendee, ranking our biotech assets against their
          stated priorities (from Perplexity enrichment).
          ════════════════════════════════════════════════════════════ */}
      <Dialog open={bulkPharmaDialog.open} onOpenChange={(o) => { if (!o && !bulkPharmaSending) setBulkPharmaDialog({ open: false, drafts: [], skipped: [], total: 0 }); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-pharma-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Mail className="w-4 h-4 text-emerald-600" />
              Review &amp; send pharma intro emails
              <span className="text-[10px] font-normal text-slate-500 ml-1">
                {bulkPharmaDialog.drafts.length} draft{bulkPharmaDialog.drafts.length === 1 ? '' : 's'} · {bulkPharmaDialog.skipped.length} skipped
              </span>
            </DialogTitle>
            <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>
                Each email pitches the Confirmed Biotech assets that best match this pharma's priorities. Edit any row's subject/body inline — the changes are picked up automatically on Send.
              </span>
              <button
                type="button"
                onClick={() => setMarkSentDialog({ open: true, value: '', submitting: false })}
                className="text-amber-700 underline hover:text-amber-900 font-medium"
                data-testid="open-mark-already-sent-btn"
                title="One-time recovery: paste email addresses you've already emailed for this campaign so dedup honours them. Use this if a previous send dispatched to Gmail but didn't log to the DB."
              >
                Already sent some emails? Mark them →
              </button>
            </p>
          </DialogHeader>

          {bulkPharmaLoading && bulkPharmaDialog.drafts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-xs">Loading drafts — running AI only for pharmas that don't have one cached…</p>
              <p className="text-[10px] text-slate-400 mt-1">Cached rows return instantly · fresh rows ~10-20s each · 4 in flight at once</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-2 py-2 pr-1 relative">
              {bulkPharmaSending && (
                <div
                  className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center rounded-md border-2 border-sky-300 shadow-lg"
                  data-testid="bulk-pharma-sending-overlay"
                >
                  <Loader2 className="w-12 h-12 animate-spin text-sky-600 mb-4" />
                  <p className="text-lg font-bold text-slate-900 mb-1">
                    Sending emails — please wait…
                  </p>
                  <p className="text-sm text-slate-600 mb-2">
                    Dispatching via Gmail in parallel · this can take 30s – 3 min depending on volume
                  </p>
                  {bulkPharmaProgress && (
                    <div
                      className="mt-2 px-4 py-2 bg-sky-50 border border-sky-200 rounded text-sm font-mono text-slate-800"
                      data-testid="bulk-pharma-progress"
                    >
                      <span className="text-emerald-700 font-bold">{bulkPharmaProgress.sent || 0}</span>
                      <span className="text-slate-500"> sent · </span>
                      <span className="text-rose-700 font-bold">{bulkPharmaProgress.failed || 0}</span>
                      <span className="text-slate-500"> failed · </span>
                      <span className="text-slate-700 font-bold">{bulkPharmaProgress.total || 0}</span>
                      <span className="text-slate-500"> total</span>
                      {(bulkPharmaProgress.already_sent || 0) > 0 && (
                        <span className="text-slate-500"> · {bulkPharmaProgress.already_sent} already-sent skipped</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-rose-600 font-semibold tracking-wide uppercase mt-2">
                    ⚠ Please do not close this tab or refresh until you see the confirmation
                  </p>
                </div>
              )}
              {bulkPharmaLastSend && (
                <div
                  className="bg-emerald-100 border-2 border-emerald-400 rounded-md p-4 flex items-start gap-3 shadow-md"
                  data-testid="bulk-pharma-real-send-banner"
                >
                  <span className="text-emerald-700 text-2xl leading-none">✓</span>
                  <div className="flex-1">
                    <p className="text-emerald-900 font-bold text-base">
                      DONE — {bulkPharmaLastSend.sent} real email{bulkPharmaLastSend.sent === 1 ? '' : 's'} dispatched via Gmail at {bulkPharmaLastSend.when}
                    </p>
                    {bulkPharmaLastSend.failed > 0 && (
                      <p className="text-rose-700 font-semibold text-xs mt-1">
                        ⚠ {bulkPharmaLastSend.failed} email{bulkPharmaLastSend.failed === 1 ? '' : 's'} failed — check the Gmail Sent folder for what actually went out
                      </p>
                    )}
                    {bulkPharmaLastSend.already_sent > 0 && (
                      <p className="text-amber-800 font-semibold text-xs mt-1" data-testid="bulk-pharma-already-sent-info">
                        ✓ {bulkPharmaLastSend.already_sent} recipient{bulkPharmaLastSend.already_sent === 1 ? '' : 's'} were auto-skipped because they already received an email for this campaign (no duplicates dispatched)
                      </p>
                    )}
                    <p className="text-emerald-800 text-xs mt-1">
                      Sent across {bulkPharmaLastSend.pharma_count} pharma{bulkPharmaLastSend.pharma_count === 1 ? '' : 's'}.
                      Each recipient received a personalised "Dear [Name]," email.
                      Logged in <code className="bg-emerald-50 px-1 rounded">campaign_outreach_log</code> for audit.
                      Click <strong>Close</strong> below when you're ready.
                    </p>
                  </div>
                </div>
              )}
              {bulkPharmaLastTest && (
                <div
                  className="text-[11px] bg-emerald-50 border border-emerald-300 rounded-md p-3 flex items-start gap-2"
                  data-testid="bulk-pharma-test-success-banner"
                >
                  <span className="text-emerald-700 text-base leading-none mt-0.5">✓</span>
                  <div className="flex-1">
                    <p className="text-emerald-900 font-semibold">
                      {bulkPharmaLastTest.sent} test email{bulkPharmaLastTest.sent === 1 ? '' : 's'} sent to your inbox at {bulkPharmaLastTest.when}
                    </p>
                    <p className="text-emerald-800 mt-0.5">
                      Check your inbox for the [TEST → pharma] previews. When you're happy, click the <strong>green "Send all N emails now"</strong> button at the bottom — one click dispatches all real emails in parallel to all {bulkPharmaLastTest.pharma_count} pharma{bulkPharmaLastTest.pharma_count === 1 ? '' : 's'}' attendees.
                    </p>
                    <button
                      type="button"
                      onClick={() => setBulkPharmaLastTest(null)}
                      className="text-[10px] text-emerald-700 underline mt-1 hover:text-emerald-900"
                      data-testid="bulk-pharma-test-banner-dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {bulkPharmaDialog.skipped.length > 0 && (
                <details className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2" data-testid="bulk-pharma-skipped-details">
                  <summary className="cursor-pointer text-amber-800 font-medium flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> {bulkPharmaDialog.skipped.length} pharma{bulkPharmaDialog.skipped.length === 1 ? '' : 's'} skipped — click to see why
                  </summary>
                  <ul className="mt-1.5 space-y-0.5 ml-4 text-amber-900">
                    {bulkPharmaDialog.skipped.map((s, i) => (
                      <li key={i}><span className="font-semibold">{s.company_name}</span> — {s.skip_reason}</li>
                    ))}
                  </ul>
                </details>
              )}

              {bulkPharmaDialog.drafts.map((d, idx) => (
                <div
                  key={d.attendee_row_id}
                  className={`border rounded-lg p-3 ${d.approved !== false ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50/50 opacity-50'}`}
                  data-testid={`bulk-pharma-row-${d.attendee_row_id}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-bold text-slate-900">{d.company_name}</p>
                        <button
                          type="button"
                          onClick={() => updateBulkPharmaDraft(idx, { approved: d.approved === false })}
                          disabled={bulkPharmaSending}
                          className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 disabled:opacity-40 ${
                            d.approved === false
                              ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                              : 'border-slate-300 text-slate-500 hover:bg-slate-50'
                          }`}
                          data-testid={`bulk-pharma-toggle-${d.attendee_row_id}`}
                          title={d.approved === false ? 'Include this pharma in the send' : 'Exclude this pharma from this batch (the draft is kept)'}
                        >
                          {d.approved === false ? '+ Include' : '✕ Exclude'}
                        </button>
                      </div>
                      {d.rank_summary && (
                        <p className="text-[10px] italic text-violet-700 mt-0.5 flex items-start gap-1" data-testid={`bulk-pharma-rank-${d.attendee_row_id}`}>
                          <Sparkles className="w-2.5 h-2.5 mt-0.5 shrink-0" /> Pitching: {d.rank_summary}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        To: <span className="text-slate-700">
                          {(d.recipients || []).map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ') || '(no recipients)'}
                        </span>
                        <span className="ml-1.5 text-slate-400">
                          · {(d.recipients || []).length} individual email{(d.recipients || []).length === 1 ? '' : 's'}
                        </span>
                      </p>
                      {(() => {
                        const sentForRow = sentMap[d.attendee_row_id];
                        if (!sentForRow || !sentForRow.sent_emails?.length) return null;
                        const sentEmails = new Set(sentForRow.sent_emails.map(s => s.to_email));
                        const overlap = (d.recipients || []).filter(r => sentEmails.has((r.email || '').toLowerCase()));
                        if (overlap.length === 0) return null;
                        return (
                          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1 inline-flex items-center gap-1" data-testid={`bulk-pharma-already-sent-${d.attendee_row_id}`}>
                            <AlertTriangle className="w-2.5 h-2.5" />
                            <strong>Already emailed:</strong> {overlap.map(r => r.email).join(', ')} (will be auto-skipped on send)
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={d.subject}
                    onChange={(e) => updateBulkPharmaDraft(idx, { subject: e.target.value })}
                    placeholder="Subject"
                    className="w-full text-[11px] font-semibold border border-slate-200 rounded px-2 py-1 mb-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                    data-testid={`bulk-pharma-subject-${d.attendee_row_id}`}
                  />
                  <textarea
                    value={d.body}
                    onChange={(e) => updateBulkPharmaDraft(idx, { body: e.target.value })}
                    rows={8}
                    className="w-full text-[11px] font-mono border border-slate-200 rounded p-2 focus:outline-none focus:ring-1 focus:ring-emerald-300 resize-y"
                    data-testid={`bulk-pharma-body-${d.attendee_row_id}`}
                  />
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="border-t border-slate-200 pt-3 flex-row items-center justify-between gap-2">
            <Button
              variant="outline"
              disabled={bulkPharmaSending}
              onClick={() => {
                setBulkPharmaLastSend(null);
                setBulkPharmaLastTest(null);
                setBulkPharmaDialog({ open: false, drafts: [], skipped: [], total: 0 });
              }}
              className={bulkPharmaLastSend
                ? "h-9 text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold"
                : "h-9 text-xs"
              }
              data-testid="bulk-pharma-close-btn"
            >
              {bulkPharmaLastSend ? 'Done — close' : 'Close'}
            </Button>

            {/* Two big primary actions — replaces all the toggle complexity.
                LEFT (sky): always sends 3 test emails to user's inbox — first
                3 included pharmas, 1 recipient each. No ticking needed.
                RIGHT (green): real send to all included pharmas. Backend
                dedup auto-skips anyone in the outreach log, so re-clicking
                is physically safe. */}
            <div className="flex items-center gap-2">
              {(() => {
                const included = bulkPharmaDialog.drafts.filter(d => d.approved !== false && (d.recipients || []).length > 0);
                // Test = first 3 included pharmas (1 recipient each)
                const testCount = Math.min(3, included.length);
                // Real = total recipients across all included pharmas MINUS
                // those already in the outreach log (the sent-map gives us
                // the per-row sent emails; the backend ALSO dedups so this
                // count is just an honest preview).
                let remainingCount = 0;
                for (const d of included) {
                  const sentForRow = sentMap[d.attendee_row_id];
                  const sentEmails = new Set(((sentForRow?.sent_emails) || []).map(s => s.to_email));
                  for (const r of (d.recipients || [])) {
                    const em = (r.email || '').toLowerCase();
                    if (em && !sentEmails.has(em)) remainingCount += 1;
                  }
                }
                return (
                  <>
                    <Button
                      onClick={() => sendBulkPharmaEmails('test')}
                      disabled={bulkPharmaSending || bulkPharmaLoading || testCount === 0}
                      className="h-9 text-xs gap-1.5 bg-sky-600 hover:bg-sky-700 text-white"
                      data-testid="bulk-pharma-send-test-btn"
                      title="Sends 3 test emails to YOUR inbox — one per pharma, [TEST → pharma] prefix. Nothing logged. Safe."
                    >
                      {bulkPharmaSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Send {testCount} test{testCount === 1 ? '' : 's'} to me
                    </Button>
                    <Button
                      onClick={() => sendBulkPharmaEmails('real')}
                      disabled={bulkPharmaSending || bulkPharmaLoading || remainingCount === 0}
                      className="h-9 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                      data-testid="bulk-pharma-send-real-btn"
                      title={
                        remainingCount === 0
                          ? "Everyone has already been emailed for this campaign — nothing left to send"
                          : "Dispatches real emails via Gmail to all included pharmas' attendees. Backend dedup auto-skips anyone already in the outreach log — physically impossible to send duplicates."
                      }
                    >
                      {bulkPharmaSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      {remainingCount === 0
                        ? 'All recipients already emailed'
                        : `Send to ${remainingCount} remaining recipient${remainingCount === 1 ? '' : 's'}`}
                    </Button>
                  </>
                );
              })()}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ════════════════════════════════════════════════════════════
          ONE-TIME LEGACY-DRIFT RECOVERY MODAL
          User pastes email addresses they've already emailed (extracted
          manually from their Gmail Sent folder, or anywhere). Backend
          inserts audit-log rows with status='sent' so the dedup query
          honours them on the next bulk-send. Foolproof — no Gmail
          integration, no scope problems, full user control.
          ════════════════════════════════════════════════════════════ */}
      <Dialog
        open={markSentDialog.open}
        onOpenChange={(o) => { if (!o && !markSentDialog.submitting) setMarkSentDialog({ open: false, value: '', submitting: false }); }}
      >
        <DialogContent className="max-w-xl" data-testid="mark-already-sent-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Mark recipients as already sent
            </DialogTitle>
            <p className="text-[11px] text-slate-600 mt-1">
              One-time recovery for emails that went out before the durable audit log existed.
              Paste the email addresses you've already emailed for this campaign — comma, newline, or space-separated.
              We'll record them as sent so they're automatically skipped on the next dispatch.
            </p>
          </DialogHeader>
          <textarea
            value={markSentDialog.value}
            onChange={(e) => setMarkSentDialog(prev => ({ ...prev, value: e.target.value }))}
            placeholder="jd@bms.com, js@abbvie.com&#10;recipient@pfizer.com&#10;…"
            rows={8}
            disabled={markSentDialog.submitting}
            className="w-full text-xs p-2 border border-slate-300 rounded font-mono"
            data-testid="mark-already-sent-textarea"
          />
          <p className="text-[10px] text-slate-500">
            Tip: in Gmail, open Sent → search for the campaign subject → select all → use the "to" header to copy addresses.
            Or just paste a list of any email addresses; matching against this campaign's attendees is automatic.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={markSentDialog.submitting}
              onClick={() => setMarkSentDialog({ open: false, value: '', submitting: false })}
              className="h-8 text-xs"
              data-testid="mark-already-sent-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={markSentDialog.submitting || !markSentDialog.value.trim()}
              onClick={async () => {
                setMarkSentDialog(prev => ({ ...prev, submitting: true }));
                try {
                  const r = await axios.post(
                    `${API}/campaigns/${id}/mark-already-sent`,
                    { emails: markSentDialog.value },
                    { withCredentials: true, timeout: 120000 }
                  );
                  const { marked, matched_to_rows, no_matching_row, skipped_already_logged, total_input } = r.data;
                  toast.success(
                    `Recorded ${marked} of ${total_input} pasted email${total_input === 1 ? '' : 's'} as already-sent · ` +
                    `${matched_to_rows} matched to attendee rows · ${no_matching_row} not in campaign · ${skipped_already_logged} were already logged. ` +
                    `Re-open "Send mail to all" — the green button will now show only the truly remaining recipients.`,
                    { duration: 12000 }
                  );
                  setMarkSentDialog({ open: false, value: '', submitting: false });
                  setBulkPharmaDialog({ open: false, drafts: [], skipped: [], total: 0 });
                  fetchAll();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || err?.message || 'Failed to record');
                  setMarkSentDialog(prev => ({ ...prev, submitting: false }));
                }
              }}
              className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="mark-already-sent-submit-btn"
            >
              {markSentDialog.submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
              Record as already sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}


export default function CampaignsPage() {
  const { id } = useParams();
  return id ? <CampaignDetail /> : <CampaignsList />;
}
